'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';

interface SlideFeature {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}

interface Slide {
  badge: string;
  badgeIcon: React.ReactNode;
  title: string;
  subtitle: string;
  features: SlideFeature[];
  primaryBtn: {text: string;href: string;};
  secondaryBtn: {text: string;href: string;};
  bgImage: string;
  accentColor: 'red' | 'amber' | 'sky';
}

const ArrowRightIcon = () =>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
    <path fillRule="evenodd" d="M12.97 3.97a.75.75 0 0 1 1.06 0l7.5 7.5a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 1 1-1.06-1.06l6.22-6.22H3a.75.75 0 0 1 0-1.5h16.19l-6.22-6.22a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
  </svg>;


const CartIcon = () =>
<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-5 w-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
  </svg>;


const RefreshIcon = () =>
<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-4 w-4 text-red-400">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 0 0-3.7-3.7 48.678 48.678 0 0 0-7.324 0 4.006 4.006 0 0 0-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 0 0 3.7 3.7 48.656 48.656 0 0 0 7.324 0 4.006 4.006 0 0 0 3.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3-3 3" />
  </svg>;


const ShieldIcon = () =>
<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-6 w-6">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
  </svg>;


const BadgeCheckIcon = () =>
<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-6 w-6">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z" />
  </svg>;


const TagIcon = () =>
<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-6 w-6">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6Z" />
  </svg>;


const ReceiptIcon = () =>
<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-6 w-6">
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75" />
  </svg>;


const CurrencyIcon = () =>
<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-6 w-6">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z" />
  </svg>;


const BuildingIcon = () =>
<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-6 w-6">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z" />
  </svg>;


const PrinterIcon = () =>
<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-6 w-6">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m10.5 0a48.536 48.536 0 0 0-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5Zm-3 0h.008v.008H15V10.5Z" />
  </svg>;


const DollarIcon = () =>
<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-6 w-6">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>;


const slides: Slide[] = [
{
  badge: 'REFURBISHED',
  badgeIcon: <RefreshIcon />,
  title: 'Premium Refurbished Printers, Remarkable Value',
  subtitle: 'High performance. Fully tested. Great prices.',
  accentColor: 'red',
  bgImage: 'https://printhubsolution.com/_next/static/media/printerhub-refurbished-printers-banner-optimized.34500913.webp',
  features: [
  { icon: <ShieldIcon />, title: '100% Tested', subtitle: 'Quality Assured' },
  { icon: <BadgeCheckIcon />, title: 'Refurbished', subtitle: 'Like New Performance' },
  { icon: <TagIcon />, title: 'Best Value', subtitle: 'For Your Office' }],

  primaryBtn: { text: 'Shop Refurbished Printers', href: '/inkjet-printers/' },
  secondaryBtn: { text: 'View Buying Guides', href: '/guides/' }
},
{
  badge: 'VALUE DEALS',
  badgeIcon: <RefreshIcon />,
  title: 'Smart Printer Deals, Fully Tested',
  subtitle: 'Clear specs. Practical warranty support. Reliable delivery across the USA.',
  accentColor: 'amber',
  bgImage: 'https://printhubsolution.com/_next/static/media/printerhub-sale-printers-banner-optimized.e727a2e5.webp',
  features: [
  { icon: <ReceiptIcon />, title: 'Deal Pricing', subtitle: 'Lower Upfront Cost' },
  { icon: <ReceiptIcon />, title: 'Clear Specs', subtitle: 'Easy Comparison' },
  { icon: <CurrencyIcon />, title: 'Budget Ready', subtitle: 'Home and Office' }],

  primaryBtn: { text: 'Browse Office Printers', href: '/office-printers/' },
  secondaryBtn: { text: 'Compare Home Printers', href: '/home-printers/' }
},
{
  badge: 'BUSINESS READY',
  badgeIcon: <RefreshIcon />,
  title: 'Business Printers, Refurbished for Less',
  subtitle: 'Inspected all-in-one printers for documents, scanning, reports, and daily office work.',
  accentColor: 'sky',
  bgImage: 'https://printhubsolution.com/_next/static/media/printerhub-office-printers-banner-optimized.55fb2620.webp',
  features: [
  { icon: <BuildingIcon />, title: 'Office Ready', subtitle: 'Daily Workflows' },
  { icon: <PrinterIcon />, title: 'Print Scan Copy', subtitle: 'All-in-One Utility' },
  { icon: <DollarIcon />, title: 'Cost Control', subtitle: 'Less Than New' }],

  primaryBtn: { text: 'Explore Business Printers', href: '/office-printers/' },
  secondaryBtn: { text: 'See Printer Categories', href: '#printer-categories' }
}];


const accentStyles = {
  red: {
    badge: 'border-red-400/80 bg-red-950/20 text-red-100',
    icon: 'text-red-400',
    feature: 'border-red-400/50 bg-red-500/10 text-red-400',
    shadow: 'shadow-[0_0_24px_rgba(239,68,68,0.18)]'
  },
  amber: {
    badge: 'border-red-400/80 bg-red-950/20 text-red-100',
    icon: 'text-red-400',
    feature: 'border-amber-300/55 bg-amber-400/12 text-amber-300',
    shadow: 'shadow-[0_0_24px_rgba(251,191,36,0.18)]'
  },
  sky: {
    badge: 'border-red-400/80 bg-red-950/20 text-red-100',
    icon: 'text-red-400',
    feature: 'border-sky-300/55 bg-sky-400/12 text-sky-300',
    shadow: 'shadow-[0_0_24px_rgba(56,189,248,0.18)]'
  }
};

export default function HeroSlider() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const slide = slides[current];
  const accent = accentStyles[slide.accentColor];

  return (
    <section className="relative bg-neutral-950">
      <div className="overflow-hidden">
        <div
          className="relative min-h-[620px] overflow-hidden sm:min-h-[660px] lg:min-h-[680px]">
          
          {/* Background image */}
          <img
            alt=""
            src={slide.bgImage}
            className="absolute inset-0 h-full w-full object-cover" />
          
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-950/92 via-neutral-950/55 to-neutral-950/0 lg:from-neutral-950/88 lg:via-neutral-950/34" />
          <div className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-neutral-950/70 to-transparent" />

          {/* Content */}
          <div className="relative z-10 mx-auto flex min-h-[620px] w-full max-w-[96rem] items-center px-6 pt-14 pb-24 sm:min-h-[660px] sm:px-10 lg:min-h-[680px] xl:px-8">
            <div className="max-w-[35rem]">
              {/* Badge */}
              <span className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold tracking-[0.12em] uppercase shadow-sm backdrop-blur ${accent.badge}`}>
                <span className={accent.icon}><RefreshIcon /></span>
                {slide.badge}
              </span>

              {/* Title */}
              <h1 className="mt-5 max-w-[40rem] text-4xl leading-[1.12] font-semibold text-white sm:text-5xl xl:text-[3.45rem]">
                {slide.title}
              </h1>

              {/* Subtitle */}
              <p className="mt-5 max-w-[32rem] text-base leading-7 font-medium text-neutral-100 sm:text-lg">
                {slide.subtitle}
              </p>

              {/* Features */}
              <div className="mt-8 grid max-w-[34rem] gap-4 sm:grid-cols-3">
                {slide.features.map((feature, i) =>
                <div key={i} className="flex items-center gap-3 text-white">
                    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[6px] border ${accent.feature} ${accent.shadow}`}>
                      {feature.icon}
                    </span>
                    <span>
                      <span className="block text-[0.82rem] leading-5 font-bold text-white">{feature.title}</span>
                      <span className="block text-xs leading-5 text-neutral-300">{feature.subtitle}</span>
                    </span>
                  </div>
                )}
              </div>

              {/* Buttons */}
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link
                  href={slide.primaryBtn.href}
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-[5px] border border-red-300/30 bg-red-600 px-6 text-sm font-semibold text-white shadow-[0_18px_40px_rgba(127,29,29,0.32)] transition hover:bg-red-500">
                  
                  <CartIcon />
                  {slide.primaryBtn.text}
                  <ArrowRightIcon />
                </Link>
                <Link
                  href={slide.secondaryBtn.href}
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-[5px] border border-white/20 bg-white/10 px-6 text-sm font-medium text-white shadow-sm backdrop-blur transition hover:bg-white hover:text-neutral-950">
                  
                  {slide.secondaryBtn.text}
                  <ArrowRightIcon />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dots */}
      <div className="absolute bottom-8 left-1/2 z-30 flex -translate-x-1/2 items-center gap-2">
        {slides.map((_, i) =>
        <button
          key={i}
          type="button"
          onClick={() => setCurrent(i)}
          aria-label={`Go to slide ${i + 1}`}
          className={`h-2.5 rounded-full transition-all ${
          i === current ? 'w-8 bg-white' : 'w-2.5 bg-white/45 hover:bg-white/75'}`
          } />

        )}
      </div>
    </section>);

}